import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { FileCard, FilesCardQuery, GetFile } from 'services';
import { useInfiniteFilesVariables } from '../../../store/filepicker.store';

type UseUpdateCacheAfterUploadProps = {
  accepted?: 'image' | 'all';
};

const DEFAULT_PAGINATOR_PARAMS = {
  count: 0,
  currentPage: 0,
  totalPages: 0,
  hasMorePages: false,
  perPage: 15,
  total: 0,
};

export const useUpdateCacheAfterUpload = (config: UseUpdateCacheAfterUploadProps) => {
  const type = config.accepted === 'image' ? 'image' : undefined;
  const queryClient = useQueryClient();

  const variables = useInfiniteFilesVariables({ type });

  const queryKey = useMemo(() => ['filesCardQuery.infinite', variables], [variables]);

  const getPageParams = useCallback(
    (oldPageParams?: InfiniteData<FilesCardQuery>['pageParams']): InfiniteData<FilesCardQuery>['pageParams'] => {
      if (oldPageParams) {
        return oldPageParams;
      }
      return [];
    },
    [],
  );

  const getPages = useCallback(
    (newFile: FileCard, oldPages?: InfiniteData<FilesCardQuery>['pages']): InfiniteData<FilesCardQuery>['pages'] => {
      if (oldPages) {
        return oldPages.map((page, index) => {
          if (index === 0) {
            return {
              ...page,
              files: {
                paginatorInfo: page.files?.paginatorInfo || DEFAULT_PAGINATOR_PARAMS,
                data: [newFile].concat(page?.files?.data || []),
              },
            };
          }
          return page;
        });
      }
      return [];
    },
    [],
  );

  const updateFilesCacheAfterUploadInFilePicker = useCallback(
    (newFile: FileCard) => {
      queryClient.cancelQueries({ queryKey });
      const queryData = queryClient.getQueryData(queryKey);

      if (queryData && newFile) {
        queryClient.setQueryData<InfiniteData<FilesCardQuery> | undefined>(queryKey, (oldData) => {
          return {
            pageParams: getPageParams(oldData?.pageParams),
            pages: getPages(newFile, oldData?.pages),
          };
        });
      }
    },
    [queryClient, queryKey, getPageParams, getPages],
  );

  return {
    updateFilesCacheAfterUploadInFilePicker,
  };
};
